import { useState, useEffect, useRef, useCallback } from 'react';

const PULL_THRESHOLD = 80;
const RESISTANCE_FACTOR = 0.5;

export const usePullToRefresh = (onRefresh) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const isAtTop = useRef(true);
  const canPull = useRef(false);

  // Check if we're at the top of the page
  const checkIfAtTop = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    isAtTop.current = scrollTop <= 0;
    return isAtTop.current;
  }, []);

  // Touch start handler
  const handleTouchStart = useCallback((e) => {
    if (!checkIfAtTop()) return;
    
    startY.current = e.touches[0].clientY;
    canPull.current = true;
  }, [checkIfAtTop]);

  // Touch move handler
  const handleTouchMove = useCallback((e) => {
    if (!canPull.current || !isAtTop.current) return;
    
    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;
    
    if (deltaY > 0) {
      const distance = Math.min(deltaY * RESISTANCE_FACTOR, PULL_THRESHOLD * 1.5);
      setPullDistance(distance);
      setIsPulling(distance > 10);
      
      if (distance > 30) {
        try {
          e.preventDefault();
        } catch (err) {
          console.warn('Pull-to-refresh preventDefault failed:', err);
        }
      }
    } else {
      setPullDistance(0);
      setIsPulling(false);
    }
  }, []);

  // Touch end handler
  const handleTouchEnd = useCallback(() => {
    if (!canPull.current) return;
    
    const shouldRefresh = pullDistance >= PULL_THRESHOLD;
    
    if (shouldRefresh && !isRefreshing) {
      setIsRefreshing(true);
      const startTime = Date.now();
      const minDisplayTime = 500; // Minimum time to show loading indicator (ms)
      
      // Call with isManualRefresh = true for pull-to-refresh
      onRefresh(true).finally(() => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
        
        // Keep showing the indicator for at least 500ms total
        setTimeout(() => {
          setIsRefreshing(false);
        }, remainingTime);
      });
    }
    
    setPullDistance(0);
    setIsPulling(false);
    canPull.current = false;
  }, [pullDistance, isRefreshing, onRefresh]);

  // Scroll handler
  const handleScroll = useCallback(() => {
    checkIfAtTop();
  }, [checkIfAtTop]);

  // Add event listeners
  useEffect(() => {
    const addEventListeners = () => {
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });
    };

    const removeEventListeners = () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('scroll', handleScroll);
    };

    addEventListeners();

    return () => {
      removeEventListeners();
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleScroll]);

  // Manual trigger for refresh (e.g., from button click)
  const triggerRefresh = useCallback(() => {
    if (isRefreshing) return Promise.resolve();
    
    setIsRefreshing(true);
    const startTime = Date.now();
    const minDisplayTime = 500; // Minimum time to show loading indicator (ms)
    
    // Call with isManualRefresh = true for menu click
    return onRefresh(true).finally(() => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
      
      // Keep showing the indicator for at least 500ms total
      setTimeout(() => {
        setIsRefreshing(false);
      }, remainingTime);
    });
  }, [isRefreshing, onRefresh]);

  return {
    isPulling,
    pullDistance,
    isRefreshing,
    shouldShowRefreshIndicator: isPulling || isRefreshing,
    triggerRefresh
  };
};
